import type { ReactElement } from 'react';
import cx from 'clsx';
import { useField, useForm } from 'react-final-form';
import { Box, DatePicker } from '@ff-it/ui';
import { parseDate, formatDate } from '@ff-it/form';
import { format } from 'date-fns';
import { FilterChip } from './FilterChip';
import { humanizeName } from 'utilities';
import { dateCalendar, fullPicker } from './dateChip.css';

type Part = 'month' | 'year';

const cfg: Record<Part, { picker: Record<string, boolean>; label: (d: Date) => string }> = {
  month: { picker: { showMonthYearPicker: true }, label: (d) => format(d, 'MMM. yyyy') },
  year: { picker: { showYearPicker: true }, label: (d) => String(d.getFullYear()) },
};

type PartFilterChipProps = {
  name: string;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
};

function DatePartFilterChip({
  name,
  label,
  part,
  minDate,
  maxDate,
}: PartFilterChipProps & { part: Part }): ReactElement {
  const form = useForm();
  const {
    input: { value },
  } = useField(name, { subscription: { value: true } });
  const date: Date | null = value ? (formatDate(value) ?? null) : null;

  return (
    <FilterChip
      label={label ?? humanizeName(name)}
      active={!!date}
      valueNode={date ? cfg[part].label(date) : null}
      onClear={() => form.change(name, undefined)}
      width={null}
      flush
    >
      {() => (
        <Box className={cx(dateCalendar, fullPicker)} p="2">
          <DatePicker
            inline
            {...cfg[part].picker}
            selected={date}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(d: Date | null) => form.change(name, d ? parseDate(d) : undefined)}
          />
        </Box>
      )}
    </FilterChip>
  );
}

export function MonthFilterChip(props: PartFilterChipProps): ReactElement {
  return <DatePartFilterChip {...props} part="month" />;
}

export function YearFilterChip(props: PartFilterChipProps): ReactElement {
  return <DatePartFilterChip {...props} part="year" />;
}
